// ===== BEGIN FILE: src/components/report-v2/ReportSection.tsx =====
/**
 * Reusable wrapper for report sections with consistent styling
 */

import React from 'react';
import { Box, Typography, Divider } from '@mui/joy';

interface ReportSectionProps {
  title: string;
  icon?: string;
  children: React.ReactNode;
  pageBreakBefore?: boolean;
  pageBreakAfter?: boolean;
  /** Legacy: kept for backwards compat but no longer renders a screen button */
  onNavigate?: () => void;
  navigateLabel?: string;
  /** When provided, renders a print-only clickable link to the app at this tab */
  navigateTab?: string;
}

export const ReportSection: React.FC<ReportSectionProps> = ({
  title,
  icon,
  children,
  pageBreakBefore = false,
  pageBreakAfter = false,
  onNavigate,
  navigateLabel = 'Open in App →',
  navigateTab,
}) => {
  return (
    <Box
      className="report-section"
      sx={{
        mb: 4,
        '@media print': {
          // Modern CSS (Chrome 93+, Firefox 92+)
          breakBefore: pageBreakBefore ? 'page' : 'auto',
          breakAfter: pageBreakAfter ? 'page' : 'auto',
          // Legacy fallbacks
          pageBreakBefore: pageBreakBefore ? 'always' : 'auto',
          pageBreakAfter: pageBreakAfter ? 'always' : 'auto',
          // NOTE: no break-inside here — sections intentionally span pages;
          // individual cards inside have their own break-inside: avoid via pageStyle
        }
      }}
    >
      {/* Section header — className ensures heading is never orphaned from content */}
      <Box
        className="report-section-header"
        sx={{
          mb: 3,
          pb: 2,
          borderBottom: '2px solid',
          borderImage: 'linear-gradient(90deg, #5B21B6, #F59E0B, #5B21B6) 1',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Typography
          level="h2"
          sx={{
            fontSize: '1.75rem',
            fontWeight: 700,
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {icon && <span style={{ fontSize: '1.5rem' }}>{icon}</span>}
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {/* Back to Index — screen only; href="#report-index" works in HTML export; onClick handles modal */}
          <Box
            component="a"
            href="#report-index"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              document.getElementById('report-index')?.scrollIntoView({ behavior: 'smooth' });
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.25,
              py: 0.5,
              borderRadius: '20px',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.body',
              color: 'text.secondary',
              fontSize: '0.72rem',
              fontWeight: 600,
              cursor: 'pointer',
              textDecoration: 'none',
              transition: 'all 0.15s',
              '&:hover': {
                borderColor: 'primary.outlinedBorder',
                color: 'primary.plainColor',
                bgcolor: 'primary.softBg',
              },
              '@media print': { display: 'none' },
            }}
          >
            ↑ Index
          </Box>
        {navigateTab && (
          <Box
            component="a"
            href={`https://shreemastro.com/?tab=${navigateTab}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'none',
              textDecoration: 'none',
              '@media print': {
                display: 'inline-flex',
                alignItems: 'center',
                padding: '3pt 8pt',
                borderRadius: '20pt',
                border: '1pt solid #5B21B6',
                color: '#5B21B6 !important',
                fontSize: '7pt',
                fontWeight: 600,
                flexShrink: 0,
                whiteSpace: 'nowrap',
              },
            }}
          >
            {navigateLabel}
          </Box>
        )}
        </Box>
      </Box>

      {/* Section content */}
      <Box sx={{ pl: { xs: 0, sm: 2 } }}>
        {children}
      </Box>

      {/* Optional decorative divider */}
      <Divider
        sx={{
          mt: 4,
          opacity: 0.3,
          '--Divider-lineColor': 'linear-gradient(90deg, transparent, #F59E0B, transparent)',
        }}
      />
    </Box>
  );
};
